import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import {
  matchFilterString, matchFilterNumber, sortArrayBy,
} from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'

const watchEntityKeys = ['geoSuburbs']

function useGeoSuburbs(options = {}){
  const { filters } = options
  const {
    active: filterActive,
    country_code: filterCountryCode,
    geo_state_id: filterGeoStateId,
    string,
  } = filters || {}

  const {
    updatedEntities: { geoSuburbs: geoSuburbsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const geoStates = useSelector(reduxState => reduxState.entities.geoStates)
  const geoSuburbs = useSelector(reduxState => reduxState.entities.geoSuburbs)

  const filteredGeoSuburbs = useMemo(() => {
    const filtered = Object.values(geoSuburbs).filter((suburb) => {
      const {
        active, geo_state_id, id, title,
      } = suburb

      const suburbCountryCode = geoStates[geo_state_id]?.country_code

      const matchActive = filterActive ? filterActive === active.toString() : true
      const matchCountryCode = filterCountryCode ? suburbCountryCode === filterCountryCode : true
      const matchState = matchFilterNumber(geo_state_id, Number(filterGeoStateId))
      const matchTitle = matchFilterString(title, string) || matchFilterString(id.toString(), string)

      return matchActive && matchCountryCode && matchState && matchTitle
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [geoSuburbsUpdatedAt, JSON.stringify(filters)])

  const hasFilteredGeoSuburbs = !!filteredGeoSuburbs.length
  const filteredGeoSuburbsCount = filteredGeoSuburbs.length

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredGeoSuburbsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingGeoSuburbs } = useReduxAction(
    'geoSuburbs',
    'loadGeoSuburbs',
    {
      ...filtersWithOffset,
      limit,
    },
    [performHttpRequests, filtersWithOffset],
    { shouldPerformFn: ({ loading }) => performHttpRequests && !loading },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredGeoSuburbs,
    filteredGeoSuburbsCount,
    hasFilteredGeoSuburbs,
    loading: loadingGeoSuburbs,
  }
}

export default useGeoSuburbs
